import { useTheme } from '@/hooks/useTheme';
import { useQuotes } from '@/hooks/useQuotes';

/**
 * DashboardFooter Component
 *
 * Displays a branded footer image with an inspirational quote overlay.
 *
 * Features:
 * - Uses custom branding.assets.dashboardFooter if available
 * - Falls back to default footer image
 * - Loads quotes from CSV based on user's language (154 quotes, 9 languages)
 * - Displays quote with author and category hashtag
 * - Responsive design (mobile and desktop)
 * - Accessible with semantic HTML
 */

interface DashboardFooterProps {
  className?: string;
}

export const DashboardFooter = ({ className = '' }: DashboardFooterProps) => {
  const { branding } = useTheme();
  const quote = useQuotes();

  // Get footer image from branding or use default
  const footerImage = branding?.assets?.dashboardFooter || '/assets/branding/headers/dashboard-footer.webp';

  return (
    <div
      className={`relative h-32 sm:h-40 md:h-48 overflow-hidden mt-6 ${className}`}
      style={{
        marginLeft: '-1.5rem',
        marginRight: '-1.5rem',
        marginBottom: '-1.5rem',
        width: 'calc(100% + 3rem)',
        backgroundImage: `url(${footerImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Quote content */}
      <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
        {quote ? (
          <>
            <blockquote className="text-white text-lg md:text-xl font-wagner max-w-3xl">
              "{quote.text}"
            </blockquote>
            <div className="flex items-center gap-2 mt-2">
              {quote.author && (
                <cite className="text-white/80 text-sm not-italic">
                  — {quote.author}
                </cite>
              )}
              {quote.category && (
                <span className="text-white/60 text-sm">
                  #{quote.category}
                </span>
              )}
            </div>
          </>
        ) : (
          // Loading state
          <div className="text-white/60 text-sm">Loading...</div>
        )}
      </div>
    </div>
  );
};
